import type { IubendaCookie } from '@/types'

const IUBENDA_COOKIE_NAME = '_iub_cs-s3997868'
const IUBENDA_MEASUREMENT_PURPOSE = '4'

// Listens for the iubendaCookieSet event dispatched from GTM which indicates the cookie is fully formed
const waitForIubendaCookieEvent = (): Promise<void> => {
  const TIMEOUT_MS = 20000

  return new Promise((resolve, reject) => {
    const handler = () => {
      clearTimeout(timer)
      resolve()
    }

    const timer = setTimeout(() => {
      document.removeEventListener('iubendaCookieSet', handler)
      reject(new Error('Timeout waiting for iubendaCookieSet event'))
    }, TIMEOUT_MS)

    document.addEventListener('iubendaCookieSet', handler, { once: true })
  })
}

// Retrieve cookie by value and parse as an object
const getCookie = (name: string): IubendaCookie | undefined => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))

  if (!match) return undefined

  const decodedValue = decodeURIComponent(match.split('=')[1])
  try {
    return JSON.parse(decodedValue)
  } catch (e) {
    return undefined
  }
}

/**
 * Measurement consent is granted when:
 * - top-level `consent: true` (auto opt-in in non-regulated regions), or
 * - purpose 4 (Measurement) is true in a GDPR-style purposes object
 */
export function hasMeasurementConsent (cookie: IubendaCookie | undefined): boolean {
  if (!cookie) return false
  return cookie.consent === true || (cookie.purposes?.[IUBENDA_MEASUREMENT_PURPOSE] ?? false)
}

// Return iubenda consent status
export async function isMeasurementConsentGiven (): Promise<boolean> {
  // If GTM is disabled, return false immediately since iubenda won't be initialized
  if (import.meta.env.VITE_APP_GTM_ENABLED !== 'true') {
    return false
  }

  /**
   * Iubenda is set up to assign a cookie with "purposes" in the object to indicate
   * the preference granularity of the user based on what compliance is required in their country.
   *
   * Non-regulated regions receive `consent: true` without a purposes object via
   * storeConsent(true) (onPreferenceNotNeeded).
   *
   * If an existing cookie already indicates measurement consent, return immediately.
   * Otherwise, wait for the iubendaCookieSet event from GTM before reading the cookie.
   */
  const existingCookie = getCookie(IUBENDA_COOKIE_NAME)
  if (existingCookie && (existingCookie.consent === true || existingCookie.purposes)) {
    return hasMeasurementConsent(existingCookie)
  }

  return waitForIubendaCookieEvent()
    .then(() => {
      const cookieObj = getCookie(IUBENDA_COOKIE_NAME)
      return hasMeasurementConsent(cookieObj)
    })
    .catch(() => {
      console.warn('Failed to receive iubendaCookieSet event')
      return false
    })
}
